import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

const LAST_UPDATED = '4 November 2026';
const CONTACT_EMAIL = 'prashant@devclub.in';

export default function Privacy() {
    return (
        <div className="privacy page page--narrow">
            <header className="privacy__head">
                <div className="eyebrow">Legal</div>
                <h1 className="privacy__title">Privacy policy</h1>
                <p className="privacy__meta mono muted">Last updated {LAST_UPDATED}</p>
                <p className="privacy__lede">
                    ClassGrid is a timetable planner for IIT Delhi students, run by DevClub.
                    This page explains what we store, why, and how to get it removed.
                </p>
            </header>

            <section className="privacy__body">
                <h2 className="h2">What we collect</h2>

                <h3 className="h3">When you sign in with IITD</h3>
                <p>
                    Login is handled by the IITD OAuth service at{' '}
                    <code className="mono">auth.devclub.in</code>. We receive and store your
                    kerberos id, display name, and email address, and we request your hostel
                    so we can show it on your profile. We never see or store your IITD password.
                </p>

                <h3 className="h3">While you use the planner</h3>
                <ul className="privacy__list">
                    <li><strong>Your plan</strong> — the courses you picked and your tutorial/lab choices.</li>
                    <li><strong>Calendar events</strong> — personal events you create, plus reminder preferences.</li>
                    <li><strong>Attendance marks</strong> — present, absent, or excused per class session.</li>
                    <li><strong>Room markings</strong> — lecture halls you mark as occupied, and when.</li>
                    <li><strong>Course policies</strong> — the marking scheme and attendance notes you write for a course.</li>
                    <li><strong>Device token</strong> — a Firebase Cloud Messaging id so we can send you announcements, if notifications are enabled.</li>
                </ul>

                <h3 className="h3">From institute records</h3>
                <p>
                    The course catalog, your registered courses, and class rosters come from data
                    published by IIT Delhi. This is reference data about the institute timetable —
                    it is not created by us, and it is not a profile we build on you.
                </p>

                <h2 className="h2">What we do not do</h2>
                <ul className="privacy__list">
                    <li>We do not sell your data or share it with advertisers.</li>
                    <li>We do not run analytics or advertising trackers.</li>
                    <li>We do not read your device contacts, photos, files, or location.</li>
                    <li>We do not track you across other apps or websites.</li>
                </ul>

                <h2 className="h2">Who can see what</h2>
                <div className="privacy__table-wrap">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col">Data</th>
                                <th scope="col">Visible to</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Your plan, personal events, attendance, reminder settings</td>
                                <td>Only you</td>
                            </tr>
                            <tr>
                                <td>Course calendar events, room markings, course policies</td>
                                <td>Everyone using ClassGrid (author name shown)</td>
                            </tr>
                            <tr>
                                <td>Student and professor directories, course rosters</td>
                                <td>Signed-in IITD users only</td>
                            </tr>
                            <tr>
                                <td>Feedback you submit</td>
                                <td>ClassGrid admins</td>
                            </tr>
                            <tr>
                                <td>Content reports you file</td>
                                <td>ClassGrid admins</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="privacy__note">
                    Directories listing people — students and professors — require an IITD
                    login, so they are not open to anonymous visitors. Student rosters for a
                    course are visible only to signed-in IITD users, which mirrors what the
                    institute already shares with the class.
                </p>

                <h2 className="h2">How long we keep it</h2>
                <p>
                    Your data stays while you have an account. Delete your account and the
                    private rows are erased immediately — plan, personal events, attendance,
                    reminders, and device tokens. Feedback you submitted and content you posted
                    stay, but your name and kerberos id are removed from them.
                </p>

                <h2 className="h2">Deleting your account</h2>
                <p>
                    Open <Link to="/account" className="privacy__link">Account</Link> while signed in and
                    choose <strong>Delete account</strong>. It takes effect immediately and cannot
                    be undone.
                </p>
                <p>
                    If you cannot sign in, email{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="privacy__link">{CONTACT_EMAIL}</a>{' '}
                    from the address you want removed and we will delete the account by hand.
                </p>

                <h2 className="h2">Cookies</h2>
                <p>
                    We set one cookie, <code className="mono">cg_session</code>, to keep you signed
                    in. It is httpOnly, lasts 30 days, and holds nothing but your session.
                    Signing out clears it. We set no advertising or analytics cookies.
                </p>

                <h2 className="h2">Children</h2>
                <p>
                    ClassGrid is for university students. We do not knowingly collect data from
                    anyone under 16.
                </p>

                <h2 className="h2">Changes</h2>
                <p>
                    If this policy changes in a way that affects what we collect, we will say so
                    in the app before the change takes effect.
                </p>

                <h2 className="h2">Contact</h2>
                <p>
                    DevClub, IIT Delhi —{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="privacy__link">{CONTACT_EMAIL}</a>
                </p>
            </section>
        </div>
    );
}
